/**
 * useCallSession Hook
 * Handles LiveKit room connection, participant management, and track handling
 */

'use client';

import { useState, useRef, useCallback, useEffect } from 'react';
import { 
  Room, 
  RoomEvent, 
  LocalTrack, 
  RemoteParticipant, 
  RemoteTrackPublication,
  Track,
  ConnectionState,
  createLocalTracks,
  ParticipantEvent,
  TrackPublication
} from 'livekit-client';
import { isCapacitor, isAndroid, isIOS } from '@/lib/platformUtils';
import type { UseCallSessionReturn, CallParticipant, CallType } from '../types';
import { authService } from '@/lib/authService';
import { buildApiUrl } from '@/lib/platformUtils';
import { getLiveKitUrl } from '@/lib/livekitConfig';
import toast from 'react-hot-toast';

// Native plugins for audio session management
let AudioSession: any = null;
let AudioRouter: any = null;

// Initialize native plugins
async function initializePlugins() {
  if (isCapacitor()) {
    if (isIOS()) {
      try {
        const asModule = await import('@capgo/capacitor-audio-session');
        AudioSession = asModule.AudioSession;
      } catch (e) {
        console.log('[useCallSession] AudioSession not available');
      }
    }
    
    if (isAndroid()) {
      try {
        const arModule = await import('@/plugins/AudioRouter');
        AudioRouter = arModule.AudioRouter;
      } catch (e) {
        console.log('[useCallSession] AudioRouter not available');
      }
    }
  }
}

if (typeof window !== 'undefined') {
  initializePlugins();
}

function getErrorName(error: unknown): string {
  if (error instanceof Error) {
    return error.name;
  }

  if (error && typeof error === 'object' && 'name' in error) {
    return String((error as Record<string, unknown>).name ?? '');
  }

  return '';
}

function getErrorMessage(error: unknown): string {
  if (error instanceof Error) {
    return error.message;
  }

  if (error && typeof error === 'object' && 'message' in error) {
    return String((error as Record<string, unknown>).message ?? '');
  }

  return '';
}

interface UseCallSessionOptions {
  // External refs — if provided, the hook writes to these so the parent
  // (and sibling hooks like useCallControls) share the same Room/Track references.
  roomRef?: React.MutableRefObject<Room | null>;
  localTracksRef?: React.MutableRefObject<LocalTrack[]>;
  callIdRef?: React.MutableRefObject<string | null>;
  userId?: number;
  userName?: string;
  userAvatar?: string;
  
  // Callbacks
  onParticipantJoined?: (participant: CallParticipant) => void;
  onParticipantLeft?: (identity: string) => void;
  onTrackSubscribed?: (track: Track, participant: RemoteParticipant) => void;
  onTrackUnsubscribed?: (track: Track, participant: RemoteParticipant) => void;
  onConnectionStateChange?: (state: ConnectionState) => void;
  onDisconnected?: (reason?: string) => void;
  onAudioBlocked?: () => void;
  /** Called after video track is published during connectToRoom so the parent can sync UI state */
  onVideoPublished?: (enabled: boolean) => void;
  /**
   * Returns the user's current speaker preference. On Android the audio route
   * is re-applied after every remote audio track attach — must reflect the
   * user's toggle, not a hardcoded default, or mid-call track publishes
   * (IVR prompts, MoH, test-call playback) clobber the speaker choice.
   */
  getSpeakerPreference?: () => boolean;
}

export function useCallSession(options: UseCallSessionOptions = {}): UseCallSessionReturn {
  const {
    roomRef: externalRoomRef,
    localTracksRef: externalLocalTracksRef,
    onParticipantJoined,
    onParticipantLeft,
    onTrackSubscribed,
    onTrackUnsubscribed,
    onConnectionStateChange,
    onDisconnected,
    onAudioBlocked,
    onVideoPublished,
    getSpeakerPreference,
  } = options;
  
  // State
  const [isConnecting, setIsConnecting] = useState(false);
  const [isConnected, setIsConnected] = useState(false);
  const [connectionError, setConnectionError] = useState<string | null>(null);
  const [localTracks, setLocalTracks] = useState<LocalTrack[]>([]);
  const [participants, setParticipants] = useState<CallParticipant[]>([]);
  
  // Use external refs if provided, otherwise create internal ones
  const internalRoomRef = useRef<Room | null>(null);
  const internalLocalTracksRef = useRef<LocalTrack[]>([]);
  const roomRef = externalRoomRef || internalRoomRef;
  const localTracksRef = externalLocalTracksRef || internalLocalTracksRef;
  const isConnectingRef = useRef(false);
  
  // Callback refs to avoid stale closures in event handlers
  const onParticipantJoinedRef = useRef(onParticipantJoined);
  const onParticipantLeftRef = useRef(onParticipantLeft);
  const onTrackSubscribedRef = useRef(onTrackSubscribed);
  const onTrackUnsubscribedRef = useRef(onTrackUnsubscribed);
  const onConnectionStateChangeRef = useRef(onConnectionStateChange);
  const onDisconnectedRef = useRef(onDisconnected);
  const onAudioBlockedRef = useRef(onAudioBlocked);
  
  const onVideoPublishedRef = useRef(onVideoPublished);
  const getSpeakerPreferenceRef = useRef(getSpeakerPreference);

  // Keep refs in sync with latest callbacks
  useEffect(() => {
    onParticipantJoinedRef.current = onParticipantJoined;
    onParticipantLeftRef.current = onParticipantLeft;
    onTrackSubscribedRef.current = onTrackSubscribed;
    onTrackUnsubscribedRef.current = onTrackUnsubscribed;
    onConnectionStateChangeRef.current = onConnectionStateChange;
    onDisconnectedRef.current = onDisconnected;
    onAudioBlockedRef.current = onAudioBlocked;
    onVideoPublishedRef.current = onVideoPublished;
    getSpeakerPreferenceRef.current = getSpeakerPreference;
  }, [onParticipantJoined, onParticipantLeft, onTrackSubscribed, onTrackUnsubscribed, onConnectionStateChange, onDisconnected, onAudioBlocked, onVideoPublished, getSpeakerPreference]);

  /**
   * Get LiveKit token from the API
   */
  const getLivekitToken = useCallback(async (
    roomName: string, 
    callType: CallType
  ): Promise<string | null> => {
    try {
      const tokens = authService.getTokens();
      if (!tokens?.access_token) {
        console.error('[useCallSession] No access token available');
        return null;
      }
      
      const requestToken = () => {
        const currentTokens = authService.getTokens();
        return fetch(buildApiUrl('/api/v1/livekit/token'), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${currentTokens?.access_token}`
          },
          body: JSON.stringify({
            // Room name must match what the rest of the call stack uses
            // (meeting UUID for internal calls, sip-* names for carrier calls).
            // The previous `ops_call_` prefix put the joiner in a different room.
            room_name: roomName,
            call_type: callType
          })
        });
      };

      // A stale access token (app resumed from background / cold start) fails
      // auth — refresh once and retry before giving up.
      let response = await requestToken();
      if (response.status === 401 && (await authService.refreshToken())) {
        response = await requestToken();
      }

      const data = await response.json();
      
      if (!data.success) {
        console.error('[useCallSession] Token request failed:', data.error);
        return null;
      }
      
      return data.token;
    } catch (error) {
      console.error('[useCallSession] Error getting LiveKit token:', error);
      return null;
    }
  }, []);
  
  /**
   * Convert RemoteParticipant to CallParticipant
   */
  const remoteToCallParticipant = useCallback((
    participant: RemoteParticipant
  ): CallParticipant => {
    // Parse metadata if available
    let userId = 0;
    let avatarUrl: string | undefined;
    
    try {
      if (participant.metadata) {
        const meta = JSON.parse(participant.metadata);
        userId = meta.userId || meta.user_id || 0;
        avatarUrl = meta.avatar_url;
      }
    } catch (e) {
      // Ignore metadata parse errors
    }
    
    // Check if participant has any unmuted audio
    let isMuted = true;
    let hasVideo = false;
    let isScreenSharing = false;
    
    participant.audioTrackPublications.forEach(pub => {
      if (pub.track && !pub.isMuted) {
        isMuted = false;
      }
    });
    
    participant.videoTrackPublications.forEach(pub => {
      if (pub.track && pub.source === Track.Source.Camera) {
        hasVideo = true;
      }
      if (pub.track && pub.source === Track.Source.ScreenShare) {
        isScreenSharing = true;
      }
    });
    
    return {
      identity: participant.identity,
      name: participant.name || participant.identity,
      userId,
      avatar_url: avatarUrl,
      isMuted,
      isSpeaking: participant.isSpeaking,
      hasVideo,
      isScreenSharing,
      isHandRaised: false,
      joinedAt: new Date(),
      attributes: participant.attributes,
    };
  }, []);
  
  /**
   * Update participants list from room
   */
  const updateParticipants = useCallback(() => {
    if (!roomRef.current) {
      setParticipants([]);
      return;
    }
    
    const remoteParticipants = Array.from(roomRef.current.remoteParticipants.values());
    const callParticipants = remoteParticipants.map(remoteToCallParticipant);
    
    setParticipants(callParticipants);
  }, [remoteToCallParticipant, roomRef]);
  
  /**
   * Configure audio session for call (iOS/Android)
   */
  const configureAudioSession = useCallback(async (isVideo: boolean) => {
    console.log('[useCallSession] Configuring audio session, isVideo:', isVideo);
    
    try {
      // iOS: Configure AVAudioSession
      if (AudioSession && isIOS()) {
        await AudioSession.setCategory({
          category: 'playAndRecord',
          options: isVideo ? ['defaultToSpeaker', 'allowBluetooth'] : ['allowBluetooth']
        });
        await AudioSession.setActive({ active: true });
        console.log('[useCallSession] iOS AudioSession configured');
      }
      
      // Android: Set up audio routing using initForCall
      if (AudioRouter && isAndroid()) {
        try {
          await AudioRouter.initForCall({ speaker: isVideo });
          console.log('[useCallSession] Android AudioRouter initialized');
        } catch (audioErr) {
          // initForCall may not be implemented, try setSpeakerphone as fallback
          console.log('[useCallSession] AudioRouter.initForCall not available, skipping');
        }
      }
    } catch (e) {
      // Non-fatal - audio may still work through browser defaults
      console.log('[useCallSession] Audio session configuration skipped:', e);
    }
  }, []);

  /**
   * Keep remote audio elements mounted in the DOM so browser playback remains stable.
   * Detached elements can play inconsistently on desktop browsers, especially for short TTS clips.
   */
  const attachRemoteAudioElement = useCallback((audioEl: HTMLMediaElement) => {
    if (typeof document === 'undefined') {
      return;
    }

    audioEl.style.position = 'fixed';
    audioEl.style.left = '-9999px';
    audioEl.style.top = '-9999px';
    audioEl.style.width = '1px';
    audioEl.style.height = '1px';
    audioEl.style.opacity = '0';
    audioEl.style.pointerEvents = 'none';
    audioEl.setAttribute('aria-hidden', 'true');
    audioEl.setAttribute('data-total-connect-remote-audio', 'true');

    if (!audioEl.parentElement) {
      document.body.appendChild(audioEl);
    }
  }, []);
  
  /**
   * Connect to a LiveKit room
   * @param roomName - The LiveKit room name
   * @param callType - voice or video
   * @param providedToken - Optional pre-fetched token (skips token API call if provided)
   * @param providedHost - Optional LiveKit server URL (for Capacitor apps)
   */
  const connectToRoom = useCallback(async (
    roomName: string,
    callType: CallType,
    providedToken?: string,
    providedHost?: string
  ): Promise<boolean> => {
    // Prevent duplicate connections
    if (isConnectingRef.current || roomRef.current?.state === ConnectionState.Connected) {
      console.log('[useCallSession] Already connecting or connected');
      return true;
    }
    
    isConnectingRef.current = true;
    setIsConnecting(true);
    setConnectionError(null);
    
    console.log('[useCallSession] Connecting to room:', roomName, 'type:', callType);
    
    try {
      // Configure audio session before connecting
      await configureAudioSession(callType === 'video');
      
      // Use provided token or fetch one
      const token = providedToken || await getLivekitToken(roomName, callType);
      if (!token) {
        throw new Error('Failed to get LiveKit token');
      }
      
      // Create room
      // singlePeerConnection must be false for LiveKit server v1.9.8 (v0 signaling)
      // SDK v2.17.0 defaults to true which uses v1 signaling, incompatible with server v1.9.8
      const room = new Room({
        adaptiveStream: true,
        dynacast: true,
        singlePeerConnection: false,
        audioCaptureDefaults: {
          autoGainControl: true,
          echoCancellation: true,
          noiseSuppression: true,
        },
        videoCaptureDefaults: {
          resolution: {
            width: 1280,
            height: 720,
            frameRate: 30,
          },
        },
      });
      
      // Set up event listeners
      room.on(RoomEvent.ParticipantConnected, (participant: RemoteParticipant) => {
        console.log('[useCallSession] Participant connected:', participant.identity);
        const callParticipant = remoteToCallParticipant(participant);
        onParticipantJoinedRef.current?.(callParticipant);
        updateParticipants();
      });
      
      room.on(RoomEvent.ParticipantDisconnected, (participant: RemoteParticipant) => {
        console.log('[useCallSession] Participant disconnected:', participant.identity);
        onParticipantLeftRef.current?.(participant.identity);
        updateParticipants();
      });

      // SIP legs carry live call state in participant attributes (e.g.
      // 'sip.callStatus' transitions dialing → active when the callee answers).
      // Refresh the participants list so callers can react to those transitions.
      room.on(RoomEvent.ParticipantAttributesChanged, () => {
        updateParticipants();
      });
      
      room.on(RoomEvent.TrackSubscribed, (
        track: Track,
        publication: RemoteTrackPublication,
        participant: RemoteParticipant
      ) => {
        console.log('[useCallSession] Track subscribed:', track.kind, 'from:', participant.identity);
        
        // CRITICAL: Attach audio tracks to create audio elements
        // LiveKit requires explicit attachment for audio playback
        if (track.kind === Track.Kind.Audio) {
          console.log('[useCallSession] Attaching remote audio track from:', participant.identity);
          const audioEl = track.attach();

          if (!isCapacitor() && !room.canPlaybackAudio) {
            room.startAudio().catch((err: unknown) => {
              console.log('[useCallSession] Audio unlock retry deferred:', err);
            });
          }
          
          if (audioEl) {
            audioEl.volume = 1.0;
            audioEl.muted = false;
            audioEl.autoplay = true;
            audioEl.setAttribute('playsinline', 'true');
            audioEl.setAttribute('webkit-playsinline', 'true');
            attachRemoteAudioElement(audioEl);
            
            // On mobile, re-enforce earpiece after the audio element is attached.
            if (isCapacitor()) {
              console.log('[useCallSession] Audio element attached to DOM for mobile');
              
              // Re-apply the user's audio route on Android after audio attach —
              // attaching a new <audio> element can reset the OS route, so restore
              // whatever the user last chose (default: earpiece)
              if (AudioRouter && isAndroid()) {
                setTimeout(async () => {
                  try {
                    const speaker = getSpeakerPreferenceRef.current?.() ?? false;
                    await AudioRouter.setSpeakerphone({ speaker });
                    console.log('[useCallSession] Re-applied audio route after attach, speaker:', speaker);
                  } catch (e) {
                    console.log('[useCallSession] AudioRouter error:', e);
                  }
                }, 300);
              }
            }
            
            // Try to play explicitly with retry
            const attemptPlay = async (retryCount = 0): Promise<void> => {
              try {
                await audioEl.play();
                console.log('[useCallSession] Audio playback started successfully');
              } catch (err: unknown) {
                console.error(
                  '[useCallSession] Audio playback failed (attempt',
                  retryCount + 1,
                  '):',
                  getErrorName(err),
                  getErrorMessage(err),
                );
                if (retryCount < 3) {
                  setTimeout(() => attemptPlay(retryCount + 1), 200);
                } else {
                  console.error('[useCallSession] Audio playback blocked after retries');
                  onAudioBlockedRef.current?.();
                }
              }
            };
            attemptPlay();
          }
        }
        
        onTrackSubscribedRef.current?.(track, participant);
        updateParticipants();
      });
      
      room.on(RoomEvent.TrackUnsubscribed, (
        track: Track,
        publication: RemoteTrackPublication,
        participant: RemoteParticipant
      ) => {
        console.log('[useCallSession] Track unsubscribed:', track.kind, 'from:', participant.identity);
        
        // Detach and cleanup audio elements
        if (track.kind === Track.Kind.Audio) {
          const elements = track.detach();
          elements.forEach(el => {
            if (el.parentNode) {
              el.parentNode.removeChild(el);
            }
          });
          console.log('[useCallSession] Audio track detached and elements removed');
        } else {
          track.detach();
        }
        
        onTrackUnsubscribedRef.current?.(track, participant);
        updateParticipants();
      });
      
      room.on(RoomEvent.TrackMuted, () => {
        updateParticipants();
      });
      
      room.on(RoomEvent.TrackUnmuted, () => {
        updateParticipants();
      });
      
      room.on(RoomEvent.ActiveSpeakersChanged, (speakers) => {
        // Update speaking status
        setParticipants(prev => prev.map(p => ({
          ...p,
          isSpeaking: speakers.some(s => s.identity === p.identity)
        })));
      });
      
      room.on(RoomEvent.ConnectionStateChanged, (state: ConnectionState) => {
        console.log('[useCallSession] Connection state:', state);
        onConnectionStateChangeRef.current?.(state);
        
        if (state === ConnectionState.Connected) {
          setIsConnected(true);
          setIsConnecting(false);
        } else if (state === ConnectionState.Disconnected) {
          setIsConnected(false);
        }
      });
      
      room.on(RoomEvent.Disconnected, (reason) => {
        console.log('[useCallSession] Disconnected, reason:', reason);
        setIsConnected(false);
        onDisconnectedRef.current?.(reason?.toString());
      });
      
      room.on(RoomEvent.AudioPlaybackStatusChanged, () => {
        if (!room.canPlaybackAudio) {
          console.log('[useCallSession] Audio playback blocked');
          onAudioBlockedRef.current?.();
        }
      });
      
      // Connect to room
      const livekitUrl = providedHost || getLiveKitUrl();
      console.log('[useCallSession] Connecting to:', livekitUrl);
      
      await room.connect(livekitUrl, token);

      if (!isCapacitor()) {
        try {
          await room.startAudio();
          console.log('[useCallSession] Audio playback primed via room.startAudio()');
        } catch (audioErr) {
          console.log('[useCallSession] room.startAudio() deferred:', audioErr);
        }
      }
      
      roomRef.current = room;
      
      // Create and publish local tracks
      // room.connect() is async and completes when engine is ready,
      // so we can publish immediately after on modern browsers.
      // Capacitor's slower connection time (2-3s) is sufficient delay.
      const isVideo = callType === 'video';
      let tracks: LocalTrack[] = [];
      let videoPublished = false;

      // A missing microphone (NotFoundError) must not abort the call — a SIP
      // leg to the callee is already ringing by the time we get here. Join
      // without a local track; the caller stays muted but connected.
      const createVoiceTracks = async (): Promise<LocalTrack[]> => {
        try {
          return await createLocalTracks({ audio: true, video: false });
        } catch (audioErr) {
          const name = getErrorName(audioErr);
          if (name === 'NotFoundError' || name === 'DevicesNotFoundError') {
            console.warn('[useCallSession] No audio input device — joining muted:', audioErr);
            toast.error('No microphone detected — joining without audio input', { duration: 4000 });
            return [];
          }
          throw audioErr;
        }
      };

      if (isVideo) {
        // For video calls, try video+audio first; fall back to audio-only if camera fails
        try {
          // On Capacitor, request camera permissions explicitly first
          if (isCapacitor()) {
            try {
              // Warm up media permissions — on Android WebView this triggers the system permission dialog
              const testStream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
              testStream.getTracks().forEach(t => t.stop());
            } catch (permErr) {
              console.warn('[useCallSession] Camera permission check failed:', permErr);
              // Continue anyway — createLocalTracks may still work with audio only
            }
          }
          
          tracks = await createLocalTracks({
            audio: true,
            video: {
              // Use front camera by default on mobile
              facingMode: isCapacitor() ? 'user' : undefined,
            },
          });
          videoPublished = tracks.some(t => t.kind === Track.Kind.Video);
        } catch (videoErr) {
          console.warn('[useCallSession] Video track creation failed, falling back to audio-only:', videoErr);
          toast.error('Camera not available — joining with audio only', { duration: 4000, icon: '📷' });
          tracks = await createVoiceTracks();
        }
      } else {
        tracks = await createVoiceTracks();
      }
      
      localTracksRef.current = tracks;
      setLocalTracks(tracks);
      
      // Publish tracks
      for (const track of tracks) {
        await room.localParticipant.publishTrack(track);
      }
      
      // Notify parent about video state so UI (isVideoEnabled) can be synced
      onVideoPublishedRef.current?.(videoPublished);
      
      console.log('[useCallSession] Connected and published', tracks.length, 'tracks', videoPublished ? '(with video)' : '(audio only)');
      
      setIsConnecting(false);
      setIsConnected(true);
      isConnectingRef.current = false;
      
      // Initial participant update
      updateParticipants();
      
      // Post-connect track verification: handle the LiveKit SDK race condition where
      // track publications can arrive before participant join signals during transfers.
      // After a brief delay, check all remote participants for unsubscribed audio tracks
      // and force-attach any that were missed during the initial sync.
      setTimeout(() => {
        if (!room || room.state !== ConnectionState.Connected) return;
        const remotes = Array.from(room.remoteParticipants.values());
        for (const participant of remotes) {
          for (const pub of participant.audioTrackPublications.values()) {
            if (pub.track && pub.isSubscribed) {
              // Track exists and is subscribed — check if an audio element is attached
              const attached = pub.track.attachedElements;
              if (!attached || attached.length === 0) {
                console.log('[useCallSession] Re-attaching orphaned audio track from:', participant.identity);
                const audioEl = pub.track.attach();
                if (audioEl) {
                  audioEl.volume = 1.0;
                  audioEl.muted = false;
                  audioEl.autoplay = true;
                  audioEl.setAttribute('playsinline', 'true');
                  audioEl.setAttribute('webkit-playsinline', 'true');
                  attachRemoteAudioElement(audioEl);
                  audioEl.play().catch(() => {});
                }
              }
            } else if (pub.track && !pub.isSubscribed) {
              console.log('[useCallSession] Force-subscribing to unsubscribed audio track from:', participant.identity);
              pub.setSubscribed(true);
            }
          }
        }
        updateParticipants();
      }, 1500);
      
      return true;
    } catch (error: unknown) {
      const errorMessage = getErrorMessage(error);
      console.error('[useCallSession] Connection error:', error);
      console.error('[useCallSession] Error details:', {
        message: errorMessage,
        stack: error instanceof Error ? error.stack : undefined,
        name: getErrorName(error),
        roomName,
        callType,
        providedToken: providedToken ? 'provided' : 'fetching',
        providedHost: providedHost || 'default'
      });
      setConnectionError(errorMessage || 'Failed to connect');
      setIsConnecting(false);
      isConnectingRef.current = false;
      
      toast.error('Failed to connect to call', { duration: 3000 });
      return false;
    }
  }, [
    attachRemoteAudioElement,
    getLivekitToken,
    configureAudioSession,
    localTracksRef,
    remoteToCallParticipant,
    roomRef,
    updateParticipants,
  ]);
  
  /**
   * Disconnect from the room
   */
  const disconnectFromRoom = useCallback((reason?: string) => {
    console.log('[useCallSession] Disconnecting from room, reason:', reason);
    
    if (roomRef.current) {
      const room = roomRef.current;
      roomRef.current = null;
      
      try {
        // Remove all event listeners
        room.removeAllListeners();
        // Disconnect with stopTracks=true
        room.disconnect(true);
      } catch (e) {
        console.log('[useCallSession] Room cleanup error (safe to ignore):', e);
      }
    }
    
    // Stop local tracks
    localTracksRef.current.forEach(track => {
      try {
        track.stop();
      } catch (e) {
        // Ignore
      }
    });
    localTracksRef.current = [];
    setLocalTracks([]);
    
    setIsConnected(false);
    setIsConnecting(false);
    setParticipants([]);
    isConnectingRef.current = false;
  }, [localTracksRef, roomRef]);
  
  // Cleanup on unmount
  useEffect(() => {
    return () => {
      disconnectFromRoom('component_unmount');
    };
  }, [disconnectFromRoom]);
  
  return {
    room: roomRef.current,
    isConnecting,
    isConnected,
    connectionError,
    localTracks,
    participants,
    connectToRoom,
    disconnectFromRoom,
    getLivekitToken,
    updateParticipants,
  };
}

export default useCallSession;
